// ShareCardRenderer.js — Canvas API-based card generation with theme support

const THEMES = {
  ocean: {
    id: 'ocean',
    name: 'Ocean',
    gradientStops: ['#0f4c6e', '#0B3C5D', '#0a3650'],
    accent: '#F4D35E',
    accentDark: '#E0BD44',
    accentOnAccent: '#0B3C5D',
    cardBg: 'rgba(255,255,255,0.85)',
    highlightBg: 'rgba(244,211,94,0.25)',
    highlightBorder: '#E0BD44',
    labelColor: 'rgba(11,60,93,0.55)',
    valueColor: '#0B3C5D',
    subColor: 'rgba(11,60,93,0.45)',
    brandColor: '#F4D35E',
    footerBg: 'rgba(7,42,63,0.85)',
    footerText: 'rgba(255,255,255,0.6)',
    footerAccent: '#F4D35E',
    fallbackGrad: ['#0e5a7e', '#0B3C5D', '#083050'],
    swatch: ['#0B3C5D', '#F4D35E'],
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset',
    gradientStops: ['#C2491D', '#8B2500', '#5C1A1B'],
    accent: '#FF9A56',
    accentDark: '#E87D3E',
    accentOnAccent: '#5C1A1B',
    cardBg: 'rgba(255,248,240,0.88)',
    highlightBg: 'rgba(255,154,86,0.22)',
    highlightBorder: '#E87D3E',
    labelColor: 'rgba(92,26,27,0.55)',
    valueColor: '#5C1A1B',
    subColor: 'rgba(92,26,27,0.45)',
    brandColor: '#FF9A56',
    footerBg: 'rgba(60,15,10,0.88)',
    footerText: 'rgba(255,255,255,0.6)',
    footerAccent: '#FF9A56',
    fallbackGrad: ['#D4592A', '#8B2500', '#5C1A1B'],
    swatch: ['#8B2500', '#FF9A56'],
  },
  midnight: {
    id: 'midnight',
    name: 'Midnight',
    gradientStops: ['#312E81', '#1E1B4B', '#0F0E2A'],
    accent: '#A78BFA',
    accentDark: '#8B5CF6',
    accentOnAccent: '#1E1B4B',
    cardBg: 'rgba(255,255,255,0.12)',
    highlightBg: 'rgba(167,139,250,0.2)',
    highlightBorder: '#8B5CF6',
    labelColor: 'rgba(200,200,230,0.6)',
    valueColor: '#E8E4F8',
    subColor: 'rgba(200,200,230,0.5)',
    brandColor: '#A78BFA',
    footerBg: 'rgba(10,8,30,0.85)',
    footerText: 'rgba(255,255,255,0.5)',
    footerAccent: '#A78BFA',
    fallbackGrad: ['#3730A3', '#1E1B4B', '#0F0E2A'],
    swatch: ['#1E1B4B', '#A78BFA'],
  },
};

export const CARD_THEMES = THEMES;

const W = 800;
const H = 420;
const PHOTO_W = 400;

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    // Data URLs and blob URLs are same-origin — never set crossOrigin on them
    // This prevents Canvas tainting issues in production
    if (!src.startsWith('data:') && !src.startsWith('blob:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const trunc = (text, max) => (text.length > max ? text.slice(0, max - 1) + '..' : text);

// Strip all parenthetical content and keep only currency + numbers
const cleanBudgetValue = (v) => {
  let s = String(v || 'N/A');
  // Remove ALL parenthetical content (approx, descriptions, etc.)
  s = s.replace(/\s*\([^)]*\)/g, '');
  return s.trim();
};

const formatDate = (d) => {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length !== 3) return d;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(parts[2])} ${months[parseInt(parts[1])-1]} ${parts[0]}`;
};

const drawGradientBg = (ctx, x, y, w, h, theme) => {
  const grad = ctx.createLinearGradient(x, y, x + w * 0.4, y + h);
  grad.addColorStop(0, theme.gradientStops[0]);
  grad.addColorStop(0.4, theme.gradientStops[1]);
  grad.addColorStop(1, theme.gradientStops[2]);
  ctx.fillStyle = grad;
  ctx.fillRect(x, y, w, h);
};

const drawPhoto = (ctx, photoImg, theme) => {
  if (photoImg) {
    ctx.drawImage(photoImg, 0, 0, PHOTO_W, H);
  } else {
    const grad = ctx.createLinearGradient(0, 0, PHOTO_W * 0.3, H);
    grad.addColorStop(0, theme.fallbackGrad[0]);
    grad.addColorStop(0.5, theme.fallbackGrad[1]);
    grad.addColorStop(1, theme.fallbackGrad[2]);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, PHOTO_W, H);
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    ctx.font = '72px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('\u2708', PHOTO_W / 2, H / 2 + 24);
    ctx.textAlign = 'left';
  }
};

const drawThemedCard = (ctx, x, y, w, h, theme, highlight = false) => {
  roundRect(ctx, x, y, w, h, 12);
  ctx.fillStyle = highlight ? theme.highlightBg : theme.cardBg;
  ctx.fill();
  if (highlight) {
    ctx.strokeStyle = theme.highlightBorder;
    ctx.lineWidth = 2;
    ctx.stroke();
  }
};


// ===================== TRIP CARD =====================
export const generateTripCard = async (tripInput, itinerary, photoDataUrl, themeId = 'ocean') => {
  const theme = THEMES[themeId] || THEMES.ocean;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  const photoImg = await loadImage(photoDataUrl);
  drawPhoto(ctx, photoImg, theme);
  drawGradientBg(ctx, PHOTO_W, 0, W - PHOTO_W, H, theme);

  const rx = PHOTO_W + 24;
  const rw = W - PHOTO_W - 48;

  // Header
  ctx.font = '700 18px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillStyle = theme.brandColor;
  ctx.fillText('Triple A Travel Solutions', rx, 32);

  // Tag
  const tagText = 'TRIP CARD';
  ctx.font = '700 12px -apple-system, BlinkMacSystemFont, sans-serif';
  const tagW = ctx.measureText(tagText).width + 28;
  roundRect(ctx, W - 24 - tagW, 14, tagW, 28, 14);
  ctx.fillStyle = theme.accent;
  ctx.fill();
  ctx.fillStyle = theme.accentOnAccent;
  ctx.fillText(tagText, W - 24 - tagW + 14, 32);

  // City banner
  const city = tripInput?.destination_cities?.length > 1
    ? tripInput.destination_cities.join(' > ')
    : tripInput?.destination_city || 'Unknown';
  const country = tripInput?.destination_country || '';

  drawThemedCard(ctx, rx, 50, rw, 74, theme);
  ctx.font = '800 34px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillStyle = theme.valueColor;
  ctx.fillText(trunc(city, 18), rx + 18, 88);
  ctx.font = '500 18px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = theme.labelColor;
  ctx.fillText(country, rx + 18, 114);

  // Info cards
  const start = tripInput?.start_date || '';
  const end = tripInput?.end_date || '';
  const adults = tripInput?.adults || 1;
  const kids = tripInput?.kids || 0;
  const daysCount = itinerary?.days?.length || 0;
  const budget = itinerary?.estimated_total_budget || {};
  const totalRaw = typeof budget === 'object' ? budget.total : String(budget);
  const total = cleanBudgetValue(totalRaw);

  let travelersText = `${adults} adult${adults > 1 ? 's' : ''}`;
  if (kids > 0) travelersText += `, ${kids} kid${kids > 1 ? 's' : ''}`;

  const cards = [];
  if (start && end) cards.push({ label: 'DATES', value: formatDate(start), sub: `to ${formatDate(end)}` });
  cards.push({ label: 'TRAVELERS', value: travelersText, sub: daysCount > 0 ? `${daysCount} days` : '' });
  if (total) cards.push({ label: 'BUDGET', value: trunc(total, 18), highlight: true });

  const cardY = 142;
  const gap = 8;
  const cardW = (rw - gap * (cards.length - 1)) / cards.length;
  const cardH = 100;

  cards.forEach((card, i) => {
    const cx = rx + i * (cardW + gap);
    drawThemedCard(ctx, cx, cardY, cardW, cardH, theme, card.highlight);

    ctx.font = '700 10px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = theme.labelColor;
    ctx.fillText(card.label, cx + 12, cardY + 20);

    // Auto-shrink font to fit within card width
    const maxTextW = cardW - 24;
    let fontSize = 15;
    ctx.font = `700 ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
    while (ctx.measureText(card.value).width > maxTextW && fontSize > 9) {
      fontSize--;
      ctx.font = `700 ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
    }
    ctx.fillStyle = theme.valueColor;
    ctx.fillText(card.value, cx + 12, cardY + 48);

    if (card.sub) {
      ctx.font = '600 13px -apple-system, BlinkMacSystemFont, sans-serif';
      ctx.fillStyle = theme.subColor;
      ctx.fillText(card.sub, cx + 12, cardY + 70);
    }
  });

  // Footer
  const fy = H - 44;
  ctx.fillStyle = theme.accent;
  ctx.fillRect(PHOTO_W, fy - 2, W - PHOTO_W, 2);
  ctx.fillStyle = theme.footerBg;
  ctx.fillRect(PHOTO_W, fy, W - PHOTO_W, 44);

  ctx.font = '500 14px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = theme.footerText;
  ctx.fillText('tripleabooking.com', rx, fy + 27);

  ctx.fillStyle = theme.footerAccent;
  ctx.textAlign = 'right';
  ctx.fillText('Plan Your Dream Trip', W - 24, fy + 27);
  ctx.textAlign = 'left';

  return canvas.toDataURL('image/jpeg', 0.85);
};


// ===================== BUDGET CARD =====================
export const generateBudgetCard = async (tripInput, itinerary, photoDataUrl, themeId = 'ocean') => {
  const theme = THEMES[themeId] || THEMES.ocean;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  const photoImg = await loadImage(photoDataUrl);
  drawPhoto(ctx, photoImg, theme);
  drawGradientBg(ctx, PHOTO_W, 0, W - PHOTO_W, H, theme);

  const rx = PHOTO_W + 24;
  const rw = W - PHOTO_W - 48;

  // Header
  ctx.font = '700 18px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillStyle = theme.brandColor;
  ctx.fillText('Triple A Travel Solutions', rx, 32);

  // Tag
  const tagText = 'TRIP BUDGET';
  ctx.font = '700 12px -apple-system, BlinkMacSystemFont, sans-serif';
  const tagW = ctx.measureText(tagText).width + 28;
  roundRect(ctx, W - 24 - tagW, 14, tagW, 28, 14);
  ctx.fillStyle = theme.accent;
  ctx.fill();
  ctx.fillStyle = theme.accentOnAccent;
  ctx.fillText(tagText, W - 24 - tagW + 14, 32);

  // City + info banner
  const city = tripInput?.destination_cities?.length > 1
    ? tripInput.destination_cities.join(' > ')
    : tripInput?.destination_city || 'Unknown';
  const country = tripInput?.destination_country || '';
  const start = tripInput?.start_date || '';
  const end = tripInput?.end_date || '';
  const adults = tripInput?.adults || 1;
  const kids = tripInput?.kids || 0;

  drawThemedCard(ctx, rx, 48, rw, 66, theme);
  ctx.font = '800 26px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillStyle = theme.valueColor;
  ctx.fillText(trunc(`${city}, ${country}`, 22), rx + 16, 78);

  let travelersText = `${adults} adult${adults > 1 ? 's' : ''}`;
  if (kids > 0) travelersText += `, ${kids} kid${kids > 1 ? 's' : ''}`;
  const infoParts = [];
  if (start && end) infoParts.push(`${formatDate(start)} - ${formatDate(end)}`);
  infoParts.push(travelersText);
  ctx.font = '500 13px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = theme.subColor;
  ctx.fillText(infoParts.join('  |  '), rx + 16, 103);

  // Budget breakdown cards (3x2 grid: Accommodation, Food, Flights, Transport, Activities, Total)
  const budgetData = itinerary?.estimated_total_budget || {};
  const budget = typeof budgetData === 'object' ? budgetData : { total: String(budgetData) };

  const categories = [
    { label: 'ACCOMMODATION', value: cleanBudgetValue(budget.accommodation) },
    { label: 'FOOD', value: cleanBudgetValue(budget.food) },
    { label: 'FLIGHTS', value: cleanBudgetValue(budget.flights || 'N/A') },
    { label: 'TRANSPORT', value: cleanBudgetValue(budget.transport) },
    { label: 'ACTIVITIES', value: cleanBudgetValue(budget.activities) },
    { label: 'TOTAL', value: cleanBudgetValue(budget.total), highlight: true },
  ];

  const gridY = 128;
  const cw = (rw - 8) / 2;
  const ch = 58;
  const cgap = 6;

  categories.forEach((cat, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const cx = rx + col * (cw + cgap);
    const cy = gridY + row * (ch + cgap);
    drawThemedCard(ctx, cx, cy, cw, ch, theme, cat.highlight);

    ctx.font = '700 8px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = theme.labelColor;
    ctx.fillText(cat.label, cx + 12, cy + 16);

    // Auto-shrink font to fit within card
    const maxTextW = cw - 24;
    let catFontSize = cat.highlight ? 17 : 15;
    ctx.font = `700 ${catFontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
    while (ctx.measureText(cat.value).width > maxTextW && catFontSize > 10) {
      catFontSize--;
      ctx.font = `700 ${catFontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
    }
    ctx.fillStyle = theme.valueColor;
    ctx.fillText(trunc(cat.value, 18), cx + 12, cy + 38);
  });

  // Footer
  const fy = H - 44;
  ctx.fillStyle = theme.accent;
  ctx.fillRect(PHOTO_W, fy - 2, W - PHOTO_W, 2);
  ctx.fillStyle = theme.footerBg;
  ctx.fillRect(PHOTO_W, fy, W - PHOTO_W, 44);

  ctx.font = '500 14px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = theme.footerText;
  ctx.fillText('tripleabooking.com', rx, fy + 27);

  ctx.fillStyle = theme.footerAccent;
  ctx.textAlign = 'right';
  ctx.fillText('Plan Your Dream Trip', W - 24, fy + 27);
  ctx.textAlign = 'left';

  return canvas.toDataURL('image/jpeg', 0.85);
};


// ===================== JOURNAL CARD =====================
export const generateJournalCard = async (photoDataUrl, destination, country, date, photoCount, galleryUrl, themeId = 'ocean') => {
  const theme = THEMES[themeId] || THEMES.ocean;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  const photoImg = await loadImage(photoDataUrl);
  drawPhoto(ctx, photoImg, theme);
  drawGradientBg(ctx, PHOTO_W, 0, W - PHOTO_W, H, theme);

  const rx = PHOTO_W + 24;
  const rw = W - PHOTO_W - 48;

  // Header
  ctx.font = '700 18px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillStyle = theme.brandColor;
  ctx.fillText('Triple A Travel Solutions', rx, 32);

  // Tag
  const tagText = 'PHOTO JOURNAL';
  ctx.font = '700 11px -apple-system, BlinkMacSystemFont, sans-serif';
  const tagW = ctx.measureText(tagText).width + 28;
  roundRect(ctx, W - 24 - tagW, 14, tagW, 28, 14);
  ctx.fillStyle = theme.accent;
  ctx.fill();
  ctx.fillStyle = theme.accentOnAccent;
  ctx.fillText(tagText, W - 24 - tagW + 14, 32);

  // Destination banner
  drawThemedCard(ctx, rx, 50, rw, 74, theme);
  ctx.font = '800 34px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillStyle = theme.valueColor;
  ctx.fillText(trunc(destination || 'My Journal', 18), rx + 18, 88);
  ctx.font = '500 18px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = theme.labelColor;
  ctx.fillText(country || '', rx + 18, 114);

  // Info cards
  const cards = [];
  if (date) cards.push({ label: 'DATE', value: formatDate(date) });
  cards.push({ label: 'PHOTOS', value: `${photoCount} photo${photoCount !== 1 ? 's' : ''}`, highlight: true });

  const cardY = 142;
  const gap = 8;
  const cardW = (rw - gap * (cards.length - 1)) / cards.length;
  const cardH = 100;

  cards.forEach((card, i) => {
    const cx = rx + i * (cardW + gap);
    drawThemedCard(ctx, cx, cardY, cardW, cardH, theme, card.highlight);

    ctx.font = '700 10px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = theme.labelColor;
    ctx.fillText(card.label, cx + 12, cardY + 20);

    ctx.font = '700 17px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = theme.valueColor;
    ctx.fillText(card.value, cx + 12, cardY + 48);
  });

  // Gallery link - two lines for long URLs
  const displayUrl = galleryUrl || 'tripleabooking.com';
  const cleanUrl = displayUrl.replace(/^https?:\/\//, '');
  const linkBoxH = 52;
  drawThemedCard(ctx, rx, cardY + cardH + 12, rw, linkBoxH, theme);
  ctx.font = '700 9px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = theme.labelColor;
  ctx.fillText('GALLERY LINK', rx + 14, cardY + cardH + 28);
  ctx.font = '500 10px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = theme.valueColor;
  // Split URL across two lines if needed
  const maxChars = 38;
  const line1 = cleanUrl.slice(0, maxChars);
  const line2 = cleanUrl.slice(maxChars, maxChars * 2);
  ctx.fillText(line1, rx + 14, cardY + cardH + 42);
  if (line2) ctx.fillText(line2, rx + 14, cardY + cardH + 54);

  // Footer
  const fy = H - 44;
  ctx.fillStyle = theme.accent;
  ctx.fillRect(PHOTO_W, fy - 2, W - PHOTO_W, 2);
  ctx.fillStyle = theme.footerBg;
  ctx.fillRect(PHOTO_W, fy, W - PHOTO_W, 44);

  ctx.font = '500 14px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = theme.footerText;
  ctx.fillText('tripleabooking.com', rx, fy + 27);

  ctx.fillStyle = theme.footerAccent;
  ctx.textAlign = 'right';
  ctx.fillText('Travel Memories', W - 24, fy + 27);
  ctx.textAlign = 'left';

  return canvas.toDataURL('image/jpeg', 0.85);
};



// ===================== TIMELINE / STORY CARD =====================
export const generateTimelineCard = async (tripInput, summary, review, photoDataUrl, themeId = 'ocean') => {
  const theme = THEMES[themeId] || THEMES.ocean;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Photo on left (same pattern — base64 data URL avoids CORS tainting)
  const photoImg = await loadImage(photoDataUrl);
  drawPhoto(ctx, photoImg, theme);
  drawGradientBg(ctx, PHOTO_W, 0, W - PHOTO_W, H, theme);

  const rx = PHOTO_W + 24;
  const rw = W - PHOTO_W - 48;

  // Brand header
  ctx.font = '700 16px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillStyle = theme.brandColor;
  ctx.fillText('Triple A Travel Solutions', rx, 28);

  // Tag badge
  const tagText = 'TRIP STORY';
  ctx.font = '700 11px -apple-system, BlinkMacSystemFont, sans-serif';
  const tagW = ctx.measureText(tagText).width + 24;
  roundRect(ctx, W - 24 - tagW, 12, tagW, 26, 13);
  ctx.fillStyle = theme.accent;
  ctx.fill();
  ctx.fillStyle = theme.accentOnAccent;
  ctx.fillText(tagText, W - 24 - tagW + 12, 29);

  // Destination banner
  const city = tripInput?.destination_cities?.length > 1
    ? tripInput.destination_cities.join(' > ')
    : tripInput?.destination_city || 'Unknown';
  const country = tripInput?.destination_country || '';

  drawThemedCard(ctx, rx, 44, rw, 62, theme);

  // Auto-shrink city name to fit
  let cityFontSize = 30;
  ctx.font = `800 ${cityFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  while (ctx.measureText(city).width > rw - 36 && cityFontSize > 16) {
    cityFontSize--;
    ctx.font = `800 ${cityFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  }
  ctx.fillStyle = theme.valueColor;
  ctx.fillText(trunc(city, 24), rx + 18, 77);

  ctx.font = '500 14px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = theme.labelColor;
  ctx.fillText(country, rx + 18, 98);

  // Stats row: Days, Cities, Photos, Spent — compact 4-column
  const statsY = 120;
  const statsGap = 6;
  const statsW = (rw - statsGap * 3) / 4;
  const statsH = 54;

  const stats = [
    { label: 'DAYS', value: String(summary?.total_days || 0) },
    { label: 'CITIES', value: String(summary?.cities_visited?.length || 0) },
    { label: 'PHOTOS', value: String(summary?.total_photos || 0) },
    { label: 'SPENT', value: `$${(summary?.total_spent || 0).toFixed(0)}` },
  ];

  stats.forEach((stat, i) => {
    const sx = rx + i * (statsW + statsGap);
    drawThemedCard(ctx, sx, statsY, statsW, statsH, theme, i === 3);

    ctx.font = '700 8px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = theme.labelColor;
    ctx.fillText(stat.label, sx + 8, statsY + 16);

    // Auto-shrink stat value
    let statFS = 18;
    ctx.font = `800 ${statFS}px -apple-system, BlinkMacSystemFont, sans-serif`;
    while (ctx.measureText(stat.value).width > statsW - 16 && statFS > 11) {
      statFS--;
      ctx.font = `800 ${statFS}px -apple-system, BlinkMacSystemFont, sans-serif`;
    }
    ctx.fillStyle = theme.valueColor;
    ctx.fillText(stat.value, sx + 8, statsY + 40);
  });

  // Dates row
  const datesY = 188;
  drawThemedCard(ctx, rx, datesY, rw, 40, theme);
  ctx.font = '700 9px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = theme.labelColor;
  ctx.fillText('TRAVEL DATES', rx + 14, datesY + 15);
  ctx.font = '600 14px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = theme.valueColor;
  const dateStr = tripInput?.start_date && tripInput?.end_date
    ? `${formatDate(tripInput.start_date)} - ${formatDate(tripInput.end_date)}`
    : 'Dates not set';
  ctx.fillText(dateStr, rx + 14, datesY + 32);

  // Review (if exists) — show rating stars and snippet
  if (review && review.rating) {
    const revY = 240;
    drawThemedCard(ctx, rx, revY, rw, 50, theme, true);
    ctx.font = '700 9px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = theme.labelColor;
    ctx.fillText('REVIEW', rx + 14, revY + 15);

    // Stars
    const rating = Math.min(5, Math.max(0, review.rating || 0));
    const starStr = '\u2605'.repeat(rating) + '\u2606'.repeat(5 - rating);
    ctx.font = '14px sans-serif';
    ctx.fillStyle = theme.accent;
    ctx.fillText(starStr, rx + 60, revY + 16);

    // Review text snippet
    ctx.font = '500 12px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = theme.valueColor;
    const reviewSnippet = `"${trunc(review.text || '', 42)}"`;
    ctx.fillText(reviewSnippet, rx + 14, revY + 38);
  }

  // Activities highlight
  const actY = review ? 302 : 246;
  if (summary?.total_activities > 0) {
    drawThemedCard(ctx, rx, actY, rw, 34, theme);
    ctx.font = '700 9px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = theme.labelColor;
    ctx.fillText('ACTIVITIES', rx + 14, actY + 14);
    ctx.font = '600 13px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = theme.valueColor;
    ctx.fillText(`${summary.total_activities} activities across ${summary.total_days} days`, rx + 14, actY + 28);
  }

  // Footer
  const fy = H - 44;
  ctx.fillStyle = theme.accent;
  ctx.fillRect(PHOTO_W, fy - 2, W - PHOTO_W, 2);
  ctx.fillStyle = theme.footerBg;
  ctx.fillRect(PHOTO_W, fy, W - PHOTO_W, 44);

  ctx.font = '500 14px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = theme.footerText;
  ctx.fillText('tripleabooking.com', rx, fy + 27);

  ctx.fillStyle = theme.footerAccent;
  ctx.textAlign = 'right';
  ctx.fillText('My Trip Story', W - 24, fy + 27);
  ctx.textAlign = 'left';

  return canvas.toDataURL('image/jpeg', 0.85);
};
